import { RateLimitError, GenerationOutcomeUnknownError, serverGenerateWithRetry, type ServerGenerateResponse } from '../../services/geminiTransport';
import type { FourBeatGenerationPolicy, FourBeatTransportDiagnostic } from '../types';
import { FourBeatCallBudgetExhaustedError } from '../types';
import {
  FOUR_BEAT_CLIENT_TRANSPORT_MAX_ATTEMPTS,
  FOUR_BEAT_MAX_LOGICAL_CALLS,
  FOUR_BEAT_MAX_UPSTREAM_GEMINI_CALLS,
  FOUR_BEAT_SERVER_MAX_UPSTREAM_PER_REQUEST,
} from './scribeThinkingPolicy.service';
import { isSceneTransportRetryable } from './majorSceneTransport.service';

const BASE_DELAY_MS = 3_000;

const delay = (ms: number) => new Promise<void>((r) => setTimeout(r, ms));

export function isFourBeatTransportRetryable(error: unknown): boolean {
  if (error instanceof GenerationOutcomeUnknownError) return false;
  if (error instanceof RateLimitError) return true;
  return isSceneTransportRetryable(error);
}

/** Narrative validation failures must not trigger transport retry. */
export function isFourBeatNarrativeFailure(error: unknown): boolean {
  if (!error || typeof error !== 'object') return false;
  const message = ((error as { message?: string }).message ?? '').toLowerCase();
  return (
    message.includes('missing decision') ||
    message.includes('missing consequence') ||
    message.includes('summary-like') ||
    message.includes('under word') ||
    message.includes('required event') ||
    message.includes('repeated introduction') ||
    message.includes('pov') ||
    message.includes('continuity') ||
    message.includes('character knowledge') ||
    message.includes('weak transition') ||
    message.includes('four-beat validation')
  );
}

export function buildLogicalFourBeatCallId(params: {
  chapterNumber: number;
  beatId?: string;
  scribeOperation: 'INITIAL_CHAPTER' | 'INSPECTOR_REWRITE';
  narrativeAttempt: number;
}): string {
  const scope = params.beatId ?? 'chapter';
  const phase = params.scribeOperation === 'INITIAL_CHAPTER' ? 'initial' : 'repair';
  return `ch${params.chapterNumber}-${scope}-${phase}-narrative${params.narrativeAttempt}`;
}

export class FourBeatCallBudgetTracker {
  logicalCallsUsed = 0;
  upstreamAttemptsUsed = 0;

  constructor(readonly chapterNumber: number) {}

  startLogicalCall(): void {
    if (this.logicalCallsUsed >= FOUR_BEAT_MAX_LOGICAL_CALLS) {
      this.throwBudgetExhausted('logical_calls');
    }
    this.logicalCallsUsed += 1;
  }

  recordUpstreamAttempt(): void {
    if (this.upstreamAttemptsUsed >= FOUR_BEAT_MAX_UPSTREAM_GEMINI_CALLS) {
      this.throwBudgetExhausted('upstream_attempts');
    }
    this.upstreamAttemptsUsed += 1;
  }

  private throwBudgetExhausted(reason: 'logical_calls' | 'upstream_attempts'): never {
    console.error(`[FOUR BEAT CALL BUDGET EXHAUSTED] ${JSON.stringify({
      chapterNumber: this.chapterNumber,
      reason,
      logicalCallsUsed: this.logicalCallsUsed,
      upstreamAttemptsUsed: this.upstreamAttemptsUsed,
      maxLogicalCalls: FOUR_BEAT_MAX_LOGICAL_CALLS,
      maxUpstreamAttempts: FOUR_BEAT_MAX_UPSTREAM_GEMINI_CALLS,
    })}`);
    throw new FourBeatCallBudgetExhaustedError('Four-beat call budget exhausted.', {
      chapterNumber: this.chapterNumber,
      reason,
      logicalCallsUsed: this.logicalCallsUsed,
      upstreamAttemptsUsed: this.upstreamAttemptsUsed,
    });
  }
}

export interface FourBeatTransportContext {
  logicalCallId: string;
  chapterNumber: number;
  beatId?: string;
  scribeOperation: 'INITIAL_CHAPTER' | 'INSPECTOR_REWRITE';
  narrativeAttempt: number;
  generationPolicy: FourBeatGenerationPolicy;
  thinkingLevel: 'LOW' | 'MEDIUM' | 'HIGH';
}

export interface FourBeatTransportCallbacks {
  callBudget: FourBeatCallBudgetTracker;
  onDiagnostic?: (diagnostic: FourBeatTransportDiagnostic) => void;
}

/**
 * Bounded transport for four-beat INITIAL_CHAPTER and targeted beat repair.
 * Client: max 2 HTTP attempts per logical call. Server: max 1 Gemini upstream per HTTP request.
 * Narrative shortfalls return to the execution gate — not transport retry.
 */
export async function generateFourBeatWithBoundedTransport(
  params: Record<string, unknown>,
  ctx: FourBeatTransportContext,
  callbacks: FourBeatTransportCallbacks,
): Promise<ServerGenerateResponse & { transportAttemptsUsed: number }> {
  callbacks.callBudget.startLogicalCall();

  let lastError: unknown;

  for (let transportAttempt = 1; transportAttempt <= FOUR_BEAT_CLIENT_TRANSPORT_MAX_ATTEMPTS; transportAttempt++) {
    callbacks.callBudget.recordUpstreamAttempt();

    callbacks.onDiagnostic?.({
      generationPolicy: ctx.generationPolicy,
      logicalCallId: ctx.logicalCallId,
      chapterNumber: ctx.chapterNumber,
      beatId: ctx.beatId,
      narrativeAttempt: ctx.narrativeAttempt,
      transportAttempt,
      serverUpstreamAttempt: FOUR_BEAT_SERVER_MAX_UPSTREAM_PER_REQUEST,
      scribeOperation: ctx.scribeOperation,
      thinkingLevel: ctx.thinkingLevel,
    });

    try {
      const response = await serverGenerateWithRetry(params, {
        policy: ctx.generationPolicy,
        operation: ctx.scribeOperation,
        agent: 'scribe',
        maxRetries: FOUR_BEAT_SERVER_MAX_UPSTREAM_PER_REQUEST,
        logicalCallId: ctx.logicalCallId,
      });
      return { ...response, transportAttemptsUsed: transportAttempt };
    } catch (error: unknown) {
      lastError = error;
      if (
        isFourBeatNarrativeFailure(error) ||
        !isFourBeatTransportRetryable(error) ||
        transportAttempt >= FOUR_BEAT_CLIENT_TRANSPORT_MAX_ATTEMPTS
      ) {
        throw error;
      }
      const backoff = BASE_DELAY_MS * transportAttempt;
      console.warn(
        `[fourBeatTransport] Retryable transport failure for ${ctx.logicalCallId} (transport ${transportAttempt}/${FOUR_BEAT_CLIENT_TRANSPORT_MAX_ATTEMPTS}). Retrying in ${backoff}ms.`,
      );
      await delay(backoff);
    }
  }

  throw lastError;
}
